import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { PageTitle } from '../../../components/PageTitle';
import { Breadcrumbs } from '../../../components/Breadcrumbs';
import { createUserBreadcrumbs } from '../../../constants/BreadCrumbs/createUserBreadcrumbs';
import Switch from '../../../components/sw';
import { routes } from '../../../constants/RouterPath';
import { TypesSelect } from '../../../types/TypesSelect';
import { CopyToClipboard } from '../../../components/CopyToClipboard';
import { Roles, roleOptions } from '../../../constants/Roles';
import { addUser } from '../../../actions/users';
import { isEqualObject } from '../../../helpers/isEqual';
import { addNotification } from '../../../actions/notifications/addNotification';
import { TypesSnackbars } from '../../../types/TypesSnackbars';
import { TypesIcon } from '../../../types/TypesIcon';
import * as S from '../EditUser/styles';
import { SubmitButtons } from '../../../components/StyledComponents';
import { UserItem } from '../EditUser/constants';
import { PageTitles } from '../../../constants/PageTitles';
import { USER_PASSWORD_HINT, USER_PASSWORD_RGX } from '../../../helpers/validation';

export const CreateUser = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const handleSubmit = (values: any) => {
        const data = {
            login: values.login,
            firstName: values.firstName,
            lastName: values.lastName,
            role: values.role,
            password: values.password,
            status: values.active ? 'ACTIVE' : 'DISABLED',
        };

        dispatch(addUser(data));
    };

    const loginRegex = /^[a-z0-9]+$/;

    const validationSchema = yup.object().shape({
        login: yup
            .string()
            .matches(loginRegex, 'Латиница (a–z, нижний регистр), цифры от 0 до 9')
            .required('Поле является обязательным'),
        password: yup
            .string()
            .matches(USER_PASSWORD_RGX, USER_PASSWORD_HINT)
            .required('Поле является обязательным'),
        role: yup
            .string()
            .required('Поле является обязательным'),
    });

    const initialValue = {
        login: '',
        firstName: '',
        lastName: '',
        role: Roles.ADMIN,
        password: '',
        active: true,
    };

    const form = useFormik<UserItem>({
        initialValues: initialValue,
        onSubmit: handleSubmit,
        validationSchema,
    });

    const handleToggle = (value: boolean) => {
        form.setFieldValue('active', value);
    };

    const handleResetForm = (values: any) => {
        history.push(routes.users);
        const equal = isEqualObject(initialValue, values);

        if (!equal) {
            dispatch(
                addNotification({
                    type: TypesSnackbars.ERROR,
                    message: {
                        title: 'Изменения отменены',
                        description: '',
                    },
                    icon: TypesIcon.SNACK_ERROR,
                }),
            );
        }
    };

    return (
        <S.RootWrapper>
            <S.Wrapper>
                <PageTitle>{PageTitles.USERS}</PageTitle>
                <Breadcrumbs breadcrumbs={createUserBreadcrumbs()} />
                <S.Root>
                    <S.MainWrapper>
                        <S.IdInfo>Общая информация</S.IdInfo>
                        <S.FieldDiv>
                            <S.InpurWrapper>
                                <S.StyledInput
                                    name="firstName"
                                    onChange={form.handleChange}
                                    value={form.values.firstName}
                                    placeholder="Имя"
                                    typeInput="text"
                                    maxLength={255}
                                    title="Имя"
                                />
                                <S.StyledInput
                                    name="lastName"
                                    onChange={form.handleChange}
                                    value={form.values.lastName}
                                    placeholder="Фамилия"
                                    typeInput="text"
                                    maxLength={255}
                                    title="Фамилия"
                                />
                            </S.InpurWrapper>

                            <S.StyledSelect
                                name="role"
                                options={roleOptions}
                                selectType={TypesSelect.DEFAULT}
                                value={form.values.role}
                                label="Роль"
                                onClick={form.setFieldValue}
                                title="Роль"
                                isTouched={form.touched.role}
                                error={form.errors.role}
                            />
                        </S.FieldDiv>

                        <S.SwitchWrapper>
                            <Switch
                                name="active"
                                value={form.values.active}
                                onChange={handleToggle}
                                type="checkbox"
                                title
                            />
                        </S.SwitchWrapper>
                    </S.MainWrapper>
                    <S.MainWrapper>
                        <S.IdInfo>Данные для входа</S.IdInfo>
                        <S.FieldDiv>
                            <S.Div>
                                <S.RowWrapper>
                                    <S.StyledInput
                                        name="login"
                                        onChange={form.handleChange}
                                        value={form.values.login}
                                        placeholder="Логин"
                                        typeInput="text"
                                        maxLength={255}
                                        title="Логин"
                                        isTouched={form.touched.login}
                                        error={form.errors.login}
                                        count={form.submitCount}
                                    />
                                    <S.ClipBoardWrapper>
                                        <CopyToClipboard copyText={form.values.login} />
                                    </S.ClipBoardWrapper>
                                </S.RowWrapper>

                                {!form.errors.login && (
                                    <S.FieldInfo>
                                        Латиница (a–z, нижний регистр), цифры от 0 до 9
                                    </S.FieldInfo>
                                )}
                            </S.Div>

                            <div>
                                <S.RowWrapper>
                                    <S.StyledInput
                                        name="password"
                                        onChange={form.handleChange}
                                        value={form.values.password}
                                        placeholder="Пароль"
                                        typeInput="text"
                                        maxLength={255}
                                        title="Пароль"
                                        isTouched={form.touched.password}
                                        error={form.errors.password}
                                        count={form.submitCount}
                                    />
                                    <S.ClipBoardWrapper>
                                        <CopyToClipboard copyText={form.values.password} />
                                    </S.ClipBoardWrapper>
                                </S.RowWrapper>

                                {!form.errors.password && (
                                    <S.FieldInfo>
                                        {USER_PASSWORD_HINT}
                                    </S.FieldInfo>
                                )}
                            </div>
                        </S.FieldDiv>
                    </S.MainWrapper>
                </S.Root>
            </S.Wrapper>
            <SubmitButtons reset={() => handleResetForm(form.values)} submit={form.submitForm} />
        </S.RootWrapper>
    );
};
